import React, { useState } from "react";
import { Form, Input, Card, Button, Select } from "antd";
import { useForm } from "antd/es/form/Form";
import { useTheme } from "@/components/common/ThemeProvider";
import { showWarningNotification } from "@/components/common/utils/notify";
import { useSearchCategory1 as useSearchCategory } from "@/shared/api/client/categories-controller";
import {ContentCategoryDto, FileMetaDataDto} from "@/shared/api/model";
import { useBaseQuery } from '@/hooks/useBaseQuery';
import FileUpload from "@/components/common/FileUpload";
import { useUpdateFileUpload } from "@/hooks/posts/attachment";

const { Option } = Select;

const useSearchFileCategories = (q: string, categoryType: string) => {
  const { data, isLoading, isError } = useSearchCategory({
    q,
    categoryType
  }, {
    query: {
      enabled: !!q
    }
  });
  
  return [{ data: data, isLoading, isError }];
};

const AttachmentManager: React.FC = () => {
  const [form] = useForm<FileMetaDataDto>();
  const { dark } = useTheme();
  const {mutate,isPending,isError} = useUpdateFileUpload();
  const [categoryQuery, setCategoryQuery] = useState("");
  const [{ data: categories }] = useSearchFileCategories(categoryQuery, "FILE");
  const [isNewCategory, setIsNewCategory] = useState(false);

  const clearForm = () => {
    form.resetFields();
    setIsNewCategory(false);
  };

  const onFinish = (values: FileMetaDataDto) => {
    if (!values.id) {
      showWarningNotification("Error", "No file selected");
      return;
    }
    mutate(values, {
      onSuccess: () => {
        clearForm();
      },
      onError: (error: Error) => {
        if (error.message.includes("Already Updated")) {
          showWarningNotification("Error", "This file has already been updated");
        } else {
          showWarningNotification("Error", error.message);
        }
      }
    });
  };

  const onFileUploadSuccess = (response: FileMetaDataDto) => {
    form.setFieldsValue(response);
  };

  const onCategorySearch = (value: string) => {
    setCategoryQuery(value);
  };

  const onCategoryChange = (value: string) => {
    if (value === "__new__") {
      setIsNewCategory(true);
      form.setFieldsValue({ category: "" });
    } else {
      setIsNewCategory(false);
      form.setFieldsValue({ category: value });
    }
  };

  return (
      <div className={`tabled ${dark ? 'dark' : ''}`}>
        <Card
            bordered={false}
            className={`header-solid h-full ant-list-yes ${dark ? 'bg-dark text-light' : ''}`}
            title={<h6 className="font-semibold m-0">Attachment Manager</h6>}
        >
          <Form
              form={form}
              name="attachmentForm"
              onFinish={onFinish}
              layout="horizontal"
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 16 }}
              autoComplete={"off"}
          >
            <Form.Item
                label="File"
                name="file"
                rules={[{ required: true, message: "Please upload a file" }]}
            >
              <FileUpload
                  label="Upload File"
                  name="file"
                  form={form}
                  dark={dark}
                  onSuccess={onFileUploadSuccess}
              />
            </Form.Item>

            <Form.Item
                name="fileName"
                label="Name"
                rules={[{ required: true, message: "Name is required" }]}
            >
              <Input className={dark ? 'bg-dark text-light' : ''} />
            </Form.Item>

            <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: "Category is required" }]}
            >
              {isNewCategory ? (
                  <Input className={dark ? 'bg-dark text-light' : ''} />
              ) : (
                  <Select
                      showSearch
                      placeholder="Select a category"
                      optionFilterProp="children"
                      onSearch={onCategorySearch}
                      onChange={onCategoryChange}
                      className={dark ? 'bg-dark text-light' : ''}
                  >
                    {categories?.map(cat => (
                        <Option key={cat.name} value={cat.name}>{cat.name}</Option>
                    ))}
                    <Option value="__new__">+ Add new category</Option>
                  </Select>
              )}
            </Form.Item>

            <Form.Item name="description" label="Description">
              <Input.TextArea className={dark ? 'bg-dark text-light' : ''} />
            </Form.Item>

            <Form.Item name="originalFileName" label="Original File Name">
              <Input readOnly className={dark ? 'bg-dark text-light' : ''} />
            </Form.Item>

            <Form.Item
                name="id"
                label="File ID"
                rules={[{ required: true, message: "File Id is required" }]}
            >
              <Input readOnly className={dark ? 'bg-dark text-light' : ''} />
            </Form.Item>

            {/*<Form.Item name="type" label="Type">*/}
            {/*  <Input readOnly className={dark ? 'bg-dark text-light' : ''} />*/}
            {/*</Form.Item>*/}

            <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
              <Button
                  type="primary"
                  htmlType="submit"
                  className={dark ? 'bg-blue-600 text-white' : ''}
                  loading={isPending}
              >
                Save
              </Button>
              <Button
                  onClick={clearForm}
                  className={`ml-2 ${dark ? 'bg-gray-600 text-white' : ''}`}
              >
                Clear
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
  );
};

export default AttachmentManager;